/**
 * Central config for the public viewer "experiences" (formerly "Viewer Style").
 *
 * Single source of truth for:
 *   - which experiences exist and how they're labelled in the admin UI
 *   - the behavioural layout config the public viewer renders from
 *
 * Stored on `tours.settings.viewerStyle` (the column name is kept for
 * backwards compatibility — existing saved values map 1:1 onto the `core`
 * experiences below).
 *
 * Adding a new experience here is all that's needed for it to appear in the
 * admin selector. Behaviour is driven entirely by the boolean flags on
 * `ViewerExperienceConfig`, so new experiences start as graceful wrapper
 * layouts over the existing tour data — no dead buttons; features hide when
 * their data is missing.
 */
import type { ViewerStyle, TourSettings } from '../types';

export interface ViewerExperienceConfig {
    id: ViewerStyle;
    showTopBar: boolean;
    showFloatingControls: boolean;
    showSidebar: boolean;
    showSceneDropdown: boolean;
    showBottomThumbs: boolean;
    defaultShowThumbs: boolean;
    showShare: boolean;
    showPoweredByInBar: boolean;
    compactControls: boolean;
    // ── Premium experience behaviours (additive / optional) ──
    /** Guided "Back / Next" walkthrough nav with scene progress (Story). */
    showStoryNav?: boolean;
    /** Lead-focused property details + CTA bar (Estate Agent). CTAs hide when
     *  the underlying contact data is missing. */
    showPropertyCtas?: boolean;
    /** Enlarge the floating controls for sales-suite / kiosk use (Presentation). */
    largeControls?: boolean;
    /** Auto-hide viewer chrome after a few seconds of inactivity (Presentation). */
    autoHideChrome?: boolean;
}

export type ViewerExperienceGroup = 'core' | 'premium';

export interface ViewerExperienceMeta {
    id: ViewerStyle;
    label: string;
    description: string;
    group: ViewerExperienceGroup;
}

/**
 * Admin-facing list. Order here is the order shown in the selector. Existing
 * `core` experiences must keep their ids so saved tours stay backwards
 * compatible.
 */
export const VIEWER_EXPERIENCES: ViewerExperienceMeta[] = [
    // ── Core (unchanged behaviour) ──
    { id: 'classic', group: 'core', label: 'Classic', description: 'Top bar with scene dropdown and bottom thumbnail strip' },
    { id: 'minimal', group: 'core', label: 'Minimal', description: 'Panorama-first with floating corner controls' },
    { id: 'sidebar', group: 'core', label: 'Sidebar', description: 'Persistent scene list on the left' },
    { id: 'compact', group: 'core', label: 'Compact', description: 'Stripped-down chrome — ideal for embeds and portal listings' },
    // ── Premium ──
    {
        id: 'luxury',
        group: 'premium',
        label: 'Luxury',
        description:
            'Premium minimal viewer for high-end homes. Elegant floating controls, clean branding area, smooth transitions and reduced chrome.',
    },
    {
        id: 'story',
        group: 'premium',
        label: 'Story',
        description:
            'Guided walkthrough experience. Visitors follow a curated scene order with clear "Next" navigation and optional intro/outro screens.',
    },
    {
        id: 'estate_agent',
        group: 'premium',
        label: 'Estate Agent',
        description:
            'Lead-focused property viewer. Prominent property details and CTAs such as Book Viewing, Call Agent and Download Brochure. Hide CTAs if no data exists.',
    },
    {
        id: 'presentation',
        group: 'premium',
        label: 'Presentation',
        description:
            'Full-screen sales suite/kiosk mode. Large navigation controls, auto-hide UI, optional autoplay and clean branding.',
    },
];

export const VIEWER_EXPERIENCE_CONFIGS: Record<ViewerStyle, ViewerExperienceConfig> = {
    // ── Core: behaviour is unchanged from the original viewerStyles config ──
    classic: {
        id: 'classic',
        showTopBar: true,
        showFloatingControls: false,
        showSidebar: false,
        showSceneDropdown: true,
        showBottomThumbs: true,
        defaultShowThumbs: true,
        showShare: true,
        showPoweredByInBar: true,
        compactControls: false,
    },
    minimal: {
        id: 'minimal',
        showTopBar: false,
        showFloatingControls: true,
        showSidebar: false,
        showSceneDropdown: true,
        showBottomThumbs: true,
        defaultShowThumbs: false,
        showShare: true,
        showPoweredByInBar: false,
        compactControls: false,
    },
    sidebar: {
        id: 'sidebar',
        showTopBar: true,
        showFloatingControls: false,
        showSidebar: true,
        showSceneDropdown: false,
        showBottomThumbs: false,
        defaultShowThumbs: false,
        showShare: true,
        showPoweredByInBar: true,
        compactControls: false,
    },
    compact: {
        id: 'compact',
        showTopBar: true,
        showFloatingControls: false,
        showSidebar: false,
        showSceneDropdown: true,
        showBottomThumbs: true,
        defaultShowThumbs: false,
        showShare: false,
        showPoweredByInBar: false,
        compactControls: true,
    },

    // ── Premium: graceful wrapper layouts over existing tour data ──

    // Luxury — panorama-first like Minimal but with reduced chrome (no share,
    // no powered-by). Client branding still appears via the existing corner
    // logo overlay when a brand logo is configured.
    luxury: {
        id: 'luxury',
        showTopBar: false,
        showFloatingControls: true,
        showSidebar: false,
        showSceneDropdown: true,
        showBottomThumbs: true,
        defaultShowThumbs: false,
        showShare: false,
        showPoweredByInBar: false,
        compactControls: false,
    },

    // Story — guided walkthrough. Replaces the standard controls with a
    // prominent Back/Next nav that follows the curated scene order.
    story: {
        id: 'story',
        showTopBar: false,
        showFloatingControls: false,
        showSidebar: false,
        showSceneDropdown: false,
        showBottomThumbs: false,
        defaultShowThumbs: false,
        showShare: false,
        showPoweredByInBar: false,
        compactControls: false,
        showStoryNav: true,
    },

    // Estate Agent — lead-focused. Keeps the top bar (so the existing contact
    // entry point shows) and adds a bottom property details + CTA bar. CTAs
    // render only when their data exists.
    estate_agent: {
        id: 'estate_agent',
        showTopBar: true,
        showFloatingControls: false,
        showSidebar: false,
        showSceneDropdown: true,
        showBottomThumbs: false,
        defaultShowThumbs: false,
        showShare: false,
        showPoweredByInBar: false,
        compactControls: false,
        showPropertyCtas: true,
    },

    // Presentation — sales-suite / kiosk. Large floating controls that
    // auto-hide after inactivity, clean branding, no share/powered-by chrome.
    presentation: {
        id: 'presentation',
        showTopBar: false,
        showFloatingControls: true,
        showSidebar: false,
        showSceneDropdown: true,
        showBottomThumbs: false,
        defaultShowThumbs: false,
        showShare: false,
        showPoweredByInBar: false,
        compactControls: false,
        largeControls: true,
        autoHideChrome: true,
    },
};

/** All valid stored `viewerStyle` values. */
export const VIEWER_STYLES: ViewerStyle[] = VIEWER_EXPERIENCES.map((e) => e.id);

/**
 * Back-compat alias for the previous `VIEWER_STYLE_PRESETS` export. The shape
 * is a superset (adds `group`), so existing `{ id, label, description }`
 * consumers keep working.
 */
export const VIEWER_STYLE_PRESETS = VIEWER_EXPERIENCES;

export function getViewerExperienceConfig(style: ViewerStyle): ViewerExperienceConfig {
    return VIEWER_EXPERIENCE_CONFIGS[style] ?? VIEWER_EXPERIENCE_CONFIGS.classic;
}

/**
 * Resolve the active viewer experience. Unknown / unset values fall back to
 * Compact for embeds and Classic for full public tours.
 */
export function resolveViewerExperience(settings: TourSettings | undefined, isEmbed = false): ViewerStyle {
    const raw = settings?.viewerStyle;
    if (raw && VIEWER_STYLES.includes(raw)) return raw;
    if (isEmbed) return 'compact';
    return 'classic';
}
